import { ApiError } from '@frontend-platform/api-client';
import { getSharedState } from '@frontend-platform/host-state';
import {
  type AISUser,
  fetchUsersByIds,
  formatAISUserName,
} from '@frontend-platform/user-directory';
import { API_BASE } from '@/config/routes';
import type { CalibrationRun } from '../lib/types';

// Shape of a run as it comes off the wire: `createdBy`/`updatedBy` are bare
// user UUIDs. `enrichRunsWithUsers` resolves them into `AISUser`.
type RawCalibrationRun = Omit<CalibrationRun, 'createdBy' | 'updatedBy'> & {
  createdBy: string;
  updatedBy: string;
};

// aistudio's vueLocalStorage wraps every value in JSON.stringify, so a string
// id like `abc` is stored as the literal 7 chars `"abc"`. Standalone dev
// (main.tsx) writes the raw string. Try JSON.parse first and fall back to the
// raw value so both modes work.
function readLocalString(key: string): string {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw;
    }
  } catch {
    return '';
  }
}

// Exported so sibling API modules on other proxy bases (e.g.
// `environments-api.ts`, which hits DM's `/bot/environments` rather than the
// observation-run service) resolve identity the same way without inheriting
// observation-run-specific headers like `x-version`.
//
// Identity reads @frontend-platform/host-state directly rather than going through a
// mount-props payload seeded once at mount: aistudio's `client/app.js`
// republishes `bots`/`user` on every relevant Vuex mutation (SET_CURRENT_BOT,
// SET_USER, …), so a bot switch or login while Calibration stays mounted is
// picked up on the next call here — a snapshot taken once at mount time
// never would be.
export function getBotId(): string {
  const botId = getSharedState()?.bots?.currentBot?.id;
  return botId ?? readLocalString('currentBotId');
}

export function getUserId(): string {
  const fromSharedState = getSharedState()?.user?.id;
  if (fromSharedState !== undefined && fromSharedState !== null && fromSharedState !== '') {
    return String(fromSharedState);
  }
  try {
    // aistudio stores the full user object under `user` (JSON-stringified).
    const raw = localStorage.getItem('user');
    if (raw) {
      try {
        const user = JSON.parse(raw);
        if (user && typeof user === 'object' && user.id) return String(user.id);
      } catch {
        // fall through to legacy keys
      }
    }
    return readLocalString('user.id') || readLocalString('userId') || '';
  } catch {
    return '';
  }
}

// aistudio's own `x-channel`/`x-service-desk` headers both read the same
// Vuex field (`bots.currentSupportDesk`) — see `client/services/http.js`'s
// request interceptor. No localStorage fallback exists — aistudio never
// persists this value outside Vuex, so standalone dev simply has no channel.
export function getChannel(): string {
  return getSharedState()?.bots?.currentSupportDesk ?? '';
}

// Identity + version headers the observation-run API requires. `x-version: 2`
// is specific to this service — do not reuse this for calls to other proxy
// bases (DM's `/bot/environments` doesn't expect it and silently returns an
// empty/unexpected payload rather than erroring, since the version header
// isn't a request an unrelated service knows to reject).
export function buildIdentityHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  headers.set('accept', 'application/json');
  headers.set('x-version', '2');
  const botId = getBotId();
  if (botId) headers.set('x-bot-id', botId);
  const userId = getUserId();
  if (userId) headers.set('x-user-id', userId);
  const channel = getChannel();
  if (channel) headers.set('x-channel', channel);
  return headers;
}

// Shared by every fetch wrapper in this feature (this file, metrics-api.ts,
// environments-api.ts) so the ok-check-and-throw block can't drift into
// slightly different error messages across four otherwise-identical copies.
export async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const text = await res.text().catch(() => '');
  const data: unknown = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;
  throw new ApiError(text || `Request failed: ${res.status} ${res.statusText}`, res, data, res.url);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = buildIdentityHeaders(init.headers);

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  await throwIfNotOk(res);

  // DELETE (deleteCalibrationRun) returns an empty body on success —
  // res.json() on that rejects with "Unexpected end of JSON input", which
  // would surface as a toast on an operation that actually succeeded.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Conversation-count endpoints live on aistudio's own conversation-viewer-app
// koa router (not /observation-run, and not the generic /api/resources/*
// proxy — this is registered directly on the koa app: `/api` (router/index.js)
// + `/conversation-viewer` (router/internal.js). Matches the legacy client's
// own HttpService baseURL (`endpoint: 'conversation-viewer'` in
// ConversationViewerAPI.js -> `/api/conversation-viewer/`).
const CONVERSATION_VIEWER_BASE = '/api/conversation-viewer';

// Mirrors the legacy client's own interceptors for this endpoint exactly
// (HttpService constructed with `botScoped: true, environmentScoped: false`
// — see ConversationViewerAPI.js): x-user-id, x-bot-id, x-channel. No
// x-version, no x-tab-id/x-service-desk — those are other endpoints' contract,
// not this one's.
async function viewerRequest<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const headers = new Headers();
  headers.set('content-type', 'application/json');
  headers.set('accept', 'application/json');
  const botId = getBotId();
  if (botId) headers.set('x-bot-id', botId);
  const userId = getUserId();
  if (userId) headers.set('x-user-id', userId);
  const channel = getChannel();
  if (channel) headers.set('x-channel', channel);
  const res = await fetch(`${CONVERSATION_VIEWER_BASE}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  await throwIfNotOk(res);
  // aistudio's koa `formatMiddleware` wraps every response as
  // `{ statusCode, exceptionCode, statusMessage, payload }` — the legacy
  // client's `HttpService.request()` unwraps this via `response.data.payload`.
  // Mirror that here rather than returning the raw envelope.
  const envelope = (await res.json()) as { payload?: T };
  return envelope?.payload as T;
}

export interface GetConversationCountArgs {
  env: string;
  startTime: string;
  endTime: string;
  botRefId: string | null;
  timeZone?: string;
  channelType?: string;
}

// Mirrors ConversationViewerAPI.getConversationCount — POSTs filters and
// returns the totalCount (or null on empty).
export async function getConversationCount(
  args: GetConversationCountArgs,
  signal?: AbortSignal
): Promise<number | null> {
  const payload = {
    filters: {
      env: args.env,
      startTime: args.startTime,
      endTime: args.endTime,
      botRefId: args.botRefId,
      timeZone: args.timeZone ?? 'UTC',
      channelType: args.channelType ?? '',
      pageNumber: 1,
      pageSize: 1,
      enableOpenSearch: true,
    },
  };
  const res = await viewerRequest<{ totalCount?: number | null }>(
    '/getConversationList',
    payload,
    signal
  );
  return res?.totalCount ?? null;
}

export interface GetLiveConversationCountArgs {
  startTime: string;
  endTime: string;
  sampleRate: number;
  botRefId: string | null;
}

// Mirrors ConversationViewerAPI.getLiveConversationCountForCalibration.
export async function getLiveConversationCount(
  args: GetLiveConversationCountArgs,
  signal?: AbortSignal
): Promise<number | null> {
  const res = await viewerRequest<{ estimated_sampled_count?: number | null }>(
    '/getLiveConversationCount',
    {
      botId: getBotId(),
      botReferenceId: args.botRefId,
      startTime: args.startTime,
      endTime: args.endTime,
      sampleRate: args.sampleRate,
    },
    signal
  );
  return res?.estimated_sampled_count ?? null;
}

// Mirrors legacy `calibrationRuns.createNewRun(payload, env, botRefId)` in
// aistudio: env + bot-ref-id travel as headers; payload is the run config.
export async function createCalibrationRun(
  payload: Record<string, unknown>,
  context: { env: string; botRefId?: string }
): Promise<unknown> {
  const headers = new Headers();
  headers.set('content-type', 'application/json');
  if (context.env) headers.set('env', context.env);
  if (context.botRefId) headers.set('x-bot-ref-id', context.botRefId);
  return request('', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

// Mirrors aistudio's `calibrationRuns.delete(id)` (Resource.delete) →
// DELETE /observation-run/{id}. Returns nothing on success.
export async function deleteCalibrationRun(id: string): Promise<void> {
  await request(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Mirrors aistudio's `calibrationRuns.cancelRun(id)` → POST
// /observation-run/{id}/cancel. Terminal-cancels an in-progress run: the
// backend flips it to CANCELLED, releases the parallel-run slot, and
// publishes the interrupt signal to DS. The row stays in the list — callers
// should refetch/invalidate rather than remove it locally.
export async function cancelCalibrationRun(id: string): Promise<void> {
  await request(`/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
}

function safeJsonParse(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

// Mirrors aistudio's `calibrationRuns.getRun(id)` → GET /observation-run/{id}.
// There are TWO `payload` layers to unwrap:
//   1. the API envelope `{ payload: <run> }` (same as the list endpoint), and
//   2. the run's OWN `payload` string field — a JSON blob holding overallEval /
//      metricSummaries / items / selectedConversationCount.
// We unwrap the envelope (an object), then parse + merge the run's payload
// string onto the run, exactly like RunDetails.vue's fetchRunDetails does.
export async function getCalibrationRun<T = unknown>(id: string, signal?: AbortSignal): Promise<T> {
  const raw = await request<Record<string, unknown>>(`/${encodeURIComponent(id)}`, { signal });

  // 1. Envelope unwrap: when the run is nested under `payload` as an OBJECT,
  //    that object is the run. A string `payload` means the response is already
  //    the run (its payload is the config blob handled in step 2).
  const envelopePayload = raw?.payload;
  const run: Record<string, unknown> =
    envelopePayload && typeof envelopePayload === 'object' && !Array.isArray(envelopePayload)
      ? (envelopePayload as Record<string, unknown>)
      : raw;

  // 2. Parse the run's own config blob (a JSON string) and merge it on top.
  const config = typeof run.payload === 'string' ? safeJsonParse(run.payload) : {};
  const merged = { ...run, ...config } as Record<string, unknown>;

  return merged as T;
}

// aistudio's own logged-in user is already on @frontend-platform/host-state — resolving that
// one id locally avoids a redundant /fetchUserList round trip for exactly the
// id most likely to show up in "created by"/"updated by": a user viewing
// their own runs. aistudio independently resolves the same id for its own
// unrelated features (audit trails, chat panels, …) via its own cache — this
// only avoids *our* copy of that request, not a request aistudio would make
// regardless.
function resolveCurrentUserFromSharedState(id: string): AISUser | null {
  const user = getSharedState()?.user;
  if (!user?.id || String(user.id) !== id) return null;
  return {
    id,
    name: formatAISUserName(user as Record<string, unknown>, id),
    email: typeof user.email === 'string' ? user.email : undefined,
  };
}

// Batch-resolve user UUIDs to `AISUser` via the shared directory lib, attaching
// the same auth headers the runs API uses so the host proxy can authorize it.
export async function resolveUsers(ids: string[]): Promise<Map<string, AISUser>> {
  const result = new Map<string, AISUser>();
  const remaining: string[] = [];
  for (const id of ids) {
    const currentUser = resolveCurrentUserFromSharedState(id);
    if (currentUser) result.set(id, currentUser);
    else remaining.push(id);
  }
  if (remaining.length === 0) return result;

  const headers: Record<string, string> = {};
  const botId = getBotId();
  if (botId) headers['x-bot-id'] = botId;
  const userId = getUserId();
  if (userId) headers['x-user-id'] = userId;
  const fetched = await fetchUsersByIds(remaining, { headers });
  for (const [id, user] of fetched) result.set(id, user);
  return result;
}

// Resolve each run's `createdBy`/`updatedBy` UUID into an `AISUser`, mirroring
// test-suite's `enrichResponse` and agent-tools' `useUsersByIds`: collect the
// unique ids, batch-resolve them once, then attach the user objects. Unresolved
// ids fall back to `{ id, name: id }` so the table never shows a blank owner.
async function enrichRunsWithUsers(runs: RawCalibrationRun[]): Promise<CalibrationRun[]> {
  const ids: string[] = [];
  for (const run of runs) {
    if (run.createdBy) ids.push(run.createdBy);
    if (run.updatedBy) ids.push(run.updatedBy);
  }

  const users = await resolveUsers(ids);
  const toUser = (id: string): AISUser => users.get(id) ?? { id, name: id };

  return runs.map((run) => ({
    ...run,
    createdBy: toUser(run.createdBy),
    updatedBy: toUser(run.updatedBy),
  }));
}

export async function listCalibrationRuns(signal?: AbortSignal): Promise<CalibrationRun[]> {
  const res = await request<RawCalibrationRun[] | { payload?: RawCalibrationRun[] }>('', {
    signal,
  });
  // The endpoint may return the array directly or wrapped in `{ payload: [...] }`.
  const raw = Array.isArray(res)
    ? res
    : res && typeof res === 'object' && Array.isArray(res.payload)
      ? res.payload
      : [];
  return enrichRunsWithUsers(raw);
}

/** The single active-event record the event-status endpoint returns. */
export interface RunEventStatus {
  /** Maps to CalibrationRun.runId. */
  requestId?: string;
  status?: string;
}

/**
 * Mirrors aistudio's `calibrationRuns.getEventStatus()` -> GET
 * /observation-run/event-status?event=OBSERVATION_RUN. This is the endpoint
 * aistudio's own PollingBanner polls to track an in-progress run — a single
 * cheap record, not the full run list. Use this (on an interval, while a run
 * is active) instead of re-fetching listCalibrationRuns on an interval, which
 * hits the list endpoint continuously regardless of what actually changed.
 */
export async function getRunEventStatus(signal?: AbortSignal): Promise<RunEventStatus | null> {
  const res = await request<Record<string, unknown> | undefined>(
    '/event-status?event=OBSERVATION_RUN',
    { signal }
  );
  if (!res) return null;
  // The endpoint may return the record directly or wrapped in `{ payload }`,
  // same envelope ambiguity as listCalibrationRuns above.
  const record = (res.payload && typeof res.payload === 'object' ? res.payload : res) as
    | RunEventStatus
    | undefined;
  return record ?? null;
}

// The observation-run backend hands back a report link as a raw S3 URL
// (`category.reportDownloadUrl`), which 403s in the browser once the object
// isn't public — same failure aistudio's legacy calibration download hit
// before it started exchanging the S3 key for a presigned GET via
// `POST /api/upload/download` (see agent-tools' `getPreSignedDownloadUrl` /
// heimdall's `getPreSignedDownloadUrlApi`, the same aistudio main-app
// endpoint). Parsing bucket+key out of the URL we already have avoids
// hardcoding a bucket name the observation-run service never exposes to us —
// same virtual-hosted-style parsing as aistudio's own
// `Analysis.vue#downloadReport` (`hostname.split('.s3')[0]`).
function parseS3Url(rawUrl: string): { bucket: string; key: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }
  const vHostMatch = parsed.hostname.match(/^([^.]+)\.s3[.-]/);
  const bucket = vHostMatch?.[1];
  if (!bucket) return null;
  return { bucket, key: decodeURIComponent(parsed.pathname.slice(1)) };
}

/**
 * Exchanges a raw calibration report S3 URL for a short-lived presigned GET
 * URL via the aistudio main-app's upload service. Falls back to the raw URL
 * if it isn't a recognizable S3 URL, so an unexpected shape degrades to
 * today's behavior instead of breaking the download outright.
 */
export async function getPresignedReportDownloadUrl(rawUrl: string): Promise<string> {
  const s3Location = parseS3Url(rawUrl);
  if (!s3Location) return rawUrl;

  const headers = buildIdentityHeaders();
  headers.set('content-type', 'application/json');
  // x-version is specific to the observation-run service (see
  // buildIdentityHeaders) — the upload endpoint lives on aistudio's main app
  // and doesn't expect it.
  headers.delete('x-version');

  const res = await fetch('/api/upload/download', {
    method: 'POST',
    headers,
    credentials: 'include',
    // No `fileType` field — aistudio's own `Analysis.vue#downloadReport` calls
    // `upload.getPreSignedDownloadUrl(bucket, key)` with no third argument, so
    // its request body omits the key entirely (JSON.stringify drops
    // `undefined` values). Matching that exactly rather than sending `''`.
    body: JSON.stringify({
      bucket: s3Location.bucket,
      uploadKeyPrefix: s3Location.key,
    }),
  });
  await throwIfNotOk(res);

  const data = (await res.json()) as { payload?: string | { url?: string } };
  const payload = data?.payload;
  const presignedUrl = typeof payload === 'string' ? payload : payload?.url;
  return presignedUrl || rawUrl;
}
